import { Icon, Menu, MenuItemProps, Tooltip } from '@televet/kibble-ui';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import cuid from 'cuid';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { IKeyboardShortcutElementRefs } from 'pages/Conversations/interfaces/IKeyboardShortcutElementRefs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ChannelMessageAttachmentEntityType,
  ChannelMessageAttachmentType,
  FormTemplateType,
  SortOrder,
  useGetMessageComposerFormTemplatesLazyQuery,
} from 'shared/types/graphql';
import { createUniqueId } from '../../../../utils';
import { AttachmentsSubmenu } from './AttachmentsSubmenu';

type AttachmentsMenuProps = {
  smallButton: boolean | undefined;
  isTeamChannel: boolean;
  canSendInvoices: string | boolean;
  displayInvoiceAsPopover: boolean;
  onInvoicingDrawerToggle: () => void;
  handleFileUploadClick: () => void;
  isMobile: boolean;
  onDocumentationModalOpen: () => void;
  attachments: IAttachmentOption[];
  onFormSelection: (
    attachment: IAttachmentOption,
    entityId: string,
    isSelected: boolean,
    formType?: FormTemplateType,
  ) => void;
  setAttachInvoiceClicked?: React.Dispatch<React.SetStateAction<boolean>>;
  onInvoiceAttachmentSelection: (attachment: IAttachmentOption) => void;
  shortcuts: IKeyboardShortcutElementRefs;
};

const AttachmentsMenu = ({
  smallButton,
  isTeamChannel,
  canSendInvoices,
  displayInvoiceAsPopover,
  onInvoicingDrawerToggle,
  handleFileUploadClick,
  isMobile,
  onDocumentationModalOpen,
  attachments,
  onFormSelection,
  setAttachInvoiceClicked,
  onInvoiceAttachmentSelection,
  shortcuts,
}: AttachmentsMenuProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const FormsMenuContainerRef = useRef<HTMLDivElement | null>(null);
  const [hideTooltip, setHideTooltip] = useState(false);
  const [forceOpen, setForceOpen] = useState<true | undefined>(undefined);
  const [attachmentMenuForceOpen, setAttachmentMenuForceOpen] = useState<true | undefined>(undefined);

  const [getFormTemplates, { data: formTemplatesData, loading: isLoadingFormTemplates }] =
    useGetMessageComposerFormTemplatesLazyQuery({
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  const { formOptions, formOptionsMap } = useMemo(() => {
    const formOptions = [];
    const formOptionsMap = new Map<
      string,
      {
        attachmentType: ChannelMessageAttachmentType;
        entityType: ChannelMessageAttachmentEntityType;
        entityId: string;
        id: string;
      }
    >();

    for (const formTemplate of formTemplatesData?.findManyFormTemplate || []) {
      const isSelected = attachments.some(({ value: { attachmentType, entityId } }) => {
        return attachmentType === ChannelMessageAttachmentType.FormRequest && entityId === formTemplate.id;
      });
      formOptions.push({
        label: formTemplate.title,
        value: formTemplate.id,
        isDisabled: isSelected,
      });
      formOptionsMap.set(formTemplate.id, {
        attachmentType: ChannelMessageAttachmentType.FormRequest,
        entityType: ChannelMessageAttachmentEntityType.Form,
        entityId: formTemplate.id,
        id: formTemplate.id,
      });
    }

    return { formOptions, formOptionsMap };
  }, [formTemplatesData, attachments]);

  const handleLoadFormTemplateOptions = useCallback((): void => {
    setHideTooltip(true);
    if (currentClinicId) {
      getFormTemplates({
        variables: {
          where: {
            clinicId: { equals: currentClinicId },
            isDeleted: { equals: false },
            isActive: { equals: true },
            formTemplateType: {
              not: {
                equals: FormTemplateType.WidgetRequest,
              },
            },
          },
          orderBy: [{ title: SortOrder.Asc }],
        },
      });
    }
  }, [currentClinicId, getFormTemplates]);

  const onFormAttachmentSelect = useCallback(
    (selectedItem: MenuItemProps) => {
      const option = formOptionsMap.get(selectedItem.value || '');

      if (!option) {
        return;
      }

      const newForm = formTemplatesData?.findManyFormTemplate?.find((t) => t.id === option.entityId);
      const newAttachment: IAttachmentOption | undefined = {
        value: option,
        id: cuid(),
        formTemplateContent: newForm?.content || '',
        isFillInTheBlankForm: newForm?.formTemplateType === FormTemplateType.FillInTheBlank,
      };

      // Nasty, but there's no other way to close the parent menu
      FormsMenuContainerRef?.current?.focus();
      const event = new KeyboardEvent('keydown', { key: 'Esc', bubbles: true });
      FormsMenuContainerRef?.current?.dispatchEvent(event);

      onFormSelection(newAttachment, option.entityId, !!selectedItem.isSelected, newForm?.formTemplateType);
    },
    [formOptionsMap, formTemplatesData?.findManyFormTemplate, onFormSelection],
  );

  const hasInvoiceAttachment = useMemo(
    () => attachments.some(({ value }) => value.attachmentType === ChannelMessageAttachmentType.Invoice),
    [attachments],
  );

  const onInvoiceAttachmentSelect = useCallback(() => {
    if (hasInvoiceAttachment) return;

    if (setAttachInvoiceClicked) setAttachInvoiceClicked(true);

    const newAttachment: IAttachmentOption = {
      id: createUniqueId(),
      text: 'Invoice',
      value: {
        id: cuid(),
        attachmentType: ChannelMessageAttachmentType.Invoice,
      },
    };

    onInvoiceAttachmentSelection(newAttachment);
  }, [hasInvoiceAttachment, onInvoiceAttachmentSelection, setAttachInvoiceClicked]);

  // Forms shortcut
  useEffect(() => {
    const openParentMenuAndSubMenu = (): void => {
      setForceOpen(true);
      setAttachmentMenuForceOpen(true);
      handleLoadFormTemplateOptions();
    };
    shortcuts.subscribe('f', openParentMenuAndSubMenu);

    return (): void => {
      shortcuts.unsubscribe('f', openParentMenuAndSubMenu);
    };
  }, [handleLoadFormTemplateOptions, shortcuts]);

  // File upload shortcut
  useEffect(() => {
    shortcuts.subscribe('a', handleFileUploadClick);
    return (): void => {
      shortcuts.unsubscribe('a', handleFileUploadClick);
    };
  }, [handleFileUploadClick, shortcuts]);

  // Invoice shortcut
  useEffect(() => {
    if (canSendInvoices) {
      const sendInvoice = (): void => {
        displayInvoiceAsPopover ? onInvoicingDrawerToggle() : onInvoiceAttachmentSelect();
      };

      shortcuts.subscribe('i', sendInvoice);

      return (): void => {
        shortcuts.unsubscribe('i', sendInvoice);
      };
    }
  }, [
    canSendInvoices,
    displayInvoiceAsPopover,
    hasInvoiceAttachment,
    onInvoiceAttachmentSelect,
    onInvoicingDrawerToggle,
    shortcuts,
  ]);

  return (
    <Tooltip isOpen={hideTooltip ? false : undefined} label="Attach" zIndex={1} closeDelay={200}>
      <Box>
        <Menu
          isOpen={forceOpen}
          onOpen={handleLoadFormTemplateOptions}
          containerProps={{
            onClose: (): void => {
              setForceOpen(undefined);
              setAttachmentMenuForceOpen(undefined);
              setHideTooltip(false);
            },
          }}
          dataTestId="attachments-menu"
          isLazy={true}
          placement="top"
          buttonProps={{
            variant: 'ghostNeutral',
            leftIconName: 'plus',
            showRightIcon: false,
            size: smallButton ? 'sm' : 'md',
          }}
          listProps={{
            zIndex: 3,
            isGrouped: true,
            menuItems: [
              ...(isTeamChannel
                ? []
                : [
                    {
                      groupLabel: 'Attach',
                      label: (
                        <Box ref={FormsMenuContainerRef}>
                          <Menu
                            isOpen={attachmentMenuForceOpen}
                            placement="bottom"
                            isLazy={true}
                            containerProps={{
                              onClose: (): void => {
                                setAttachmentMenuForceOpen(undefined);
                              },
                            }}
                            buttonProps={{
                              as: AttachmentsSubmenu,
                            }}
                            listProps={{
                              isLoading: isLoadingFormTemplates,
                              isSearchable: true,
                              menuItems: formOptions,
                              onSelect: onFormAttachmentSelect,
                              position: 'fixed',
                              maxH: '300px',
                              height: '300px',
                              ...(isMobile ? { bottom: '80%' } : { left: '100%', top: '-100%' }),
                            }}
                          />
                        </Box>
                      ),
                      closeOnSelect: false,
                    },
                    {
                      label: 'Invoices',
                      value: 'invoices',
                      groupLabel: 'Attach',
                      isDisabled: !canSendInvoices,
                      leftElement: <Icon name="invoice" />,
                      'data-testid': 'attach-invoice-button',
                      style: {
                        transition: 'background 0.2s',
                      },
                      onMouseEnter: (e: React.MouseEvent): void => {
                        (e.currentTarget as HTMLElement).style.background = 'var(--chakra-colors-background-primaryAlpha-hover)';
                        (e.currentTarget as HTMLElement).style.color = 'white';
                      },
                      onMouseLeave: (e: React.MouseEvent): void => {
                        (e.currentTarget as HTMLElement).style.background = '';
                        (e.currentTarget as HTMLElement).style.color = '';
                      },
                      children: canSendInvoices ? undefined : (
                        <Tooltip label="To send invoices, connect your clinic's bank account on the Payments Settings page.">
                          <Flex>
                            <Icon name="invoice" mr={3} />
                            <Text size="sm">Invoices</Text>
                          </Flex>
                        </Tooltip>
                      ),
                      onSelect: (): void => {
                        displayInvoiceAsPopover ? onInvoicingDrawerToggle() : onInvoiceAttachmentSelect();
                      },
                    },
                  ]),
              {
                label: 'Files',
                value: 'files',
                groupLabel: 'Attach',
                leftElement: <Icon name="paperclip" />,
                'data-testid': 'attach-files-button',
                onSelect: handleFileUploadClick,
              },
              ...(isMobile
                ? []
                : [
                    {
                      label: 'Documentation',
                      value: 'documentation',
                      groupLabel: 'Attach',
                      leftElement: <Icon name="article" />,
                      'data-testid': 'attach-documentation-button',
                      onSelect: onDocumentationModalOpen,
                    },
                  ]),
            ],
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default AttachmentsMenu;
